import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Button,
  Modal,
  TextInput,
  AlertService,
  ModalContent,
  ModalFooter,
  PencilIcon,
  TrashIcon,
  SimpleTextArea,
  Theme,
} from '@spoiler-alert/ui-library';
import { createUseStyles } from 'react-jss';
import VTable from '../../components/v-table/v-table';
import VRowAction from '../../components/v-table/v-row-action';
import VColumn from '../../components/v-table/v-column';
import { GET_CATEGORIES } from '../../graphql/queries';
import { CREATE_CATEGORY, UPDATE_CATEGORY, DELETE_CATEGORY } from '../../graphql/mutations';

const useStyles = createUseStyles({
  root: {
    padding: 24,
  },
  addButton: {
    marginBottom: 16,
  },
  tableContainer: {
    width: '100%',
  },
  modalContent: {
    width: 652,
    display: 'flex',
    flexDirection: 'column',
  },
  fullWidth: {
    width: '100%',
  },
  label: {
    color: Theme.grey50,
    fontSize: 12,
  },
});

const CategoryManager = () => {
  const classes = useStyles();
  const { data, loading, refetch } = useQuery(GET_CATEGORIES);
  const [createCategory, { loading: createLoading }] = useMutation(CREATE_CATEGORY);
  const [updateCategory, { loading: updateLoading }] = useMutation(UPDATE_CATEGORY);
  const [deleteCategory] = useMutation(DELETE_CATEGORY);

  const [modalOpen, setModalOpen] = useState(false);
  const [atomicCategory, setAtomicCategory] = useState({ atomicCategory: '', description: '' });

  const handleRowClick = (row) => {
    setAtomicCategory(row);
    setModalOpen(true);
  };

  const handleAdd = () => {
    setAtomicCategory({ atomicCategory: '', description: '' });
    setModalOpen(true);
  };

  const handleRemove = async (row) => {
    if (!window.confirm('Delete this category?')) return;
    await deleteCategory({ variables: { _id: row._id } });
    AlertService.alert({
      type: 'success',
      message: <span>Category deleted</span>,
      autoDismiss: true,
      dismissDelay: 3000,
    });
    refetch();
  };

  const handleSave = async () => {
    if (!atomicCategory.atomicCategory.trim() || !atomicCategory.description.trim()) {
      AlertService.alert({
        type: 'warning',
        message: <span>Both fields are required</span>,
        autoDismiss: true,
        dismissDelay: 3000,
      });
      return;
    }
    try {
      const input = {
        _id: atomicCategory._id,
        atomicCategory: atomicCategory.atomicCategory.toUpperCase(),
        description: atomicCategory.description,
      };
      if (atomicCategory?._id) {
        await updateCategory({ variables: { input } });
        AlertService.alert({
          type: 'success',
          message: <span>Category updated</span>,
          autoDismiss: true,
          dismissDelay: 3000,
        });
      } else {
        await createCategory({ variables: { input } });
        AlertService.alert({
          type: 'success',
          message: <span>Category created</span>,
          autoDismiss: true,
          dismissDelay: 3000,
        });
      }
      setModalOpen(false);
      refetch();
    } catch (err) {
      AlertService.alert({
        type: 'warning',
        message: <span>{err?.message || 'Error saving category'}</span>,
        autoDismiss: true,
        dismissDelay: 3000,
      });
    }
  };

  const columns = [
    new VColumn({
      field: 'atomicCategory',
      displayName: 'Category',
      defaultSort: true,
      defaultSortDirection: 'asc',
      resizable: true,
    }),
    new VColumn({
      field: 'description',
      displayName: 'Description',
      resizable: true,
    }),
  ];

  const rowActions = [
    <VRowAction
      key="edit"
      icon={PencilIcon}
      onClick={(row) => () => handleRowClick(row)}
      tooltipText="Edit category"
    />,
    <VRowAction
      key="remove"
      icon={TrashIcon}
      warning
      onClick={(row) => () => handleRemove(row)}
      tooltipText="Delete category"
    />,
  ];

  return (
    <div className={classes.root}>
      <h1>Category Manager</h1>
      <Button onClick={handleAdd} className={classes.addButton}>
        Add Category
      </Button>
      <div className={classes.tableContainer}>
        <VTable
          data={data?.adminAtomicCategoriesQuery || []}
          cols={columns}
          loading={loading}
          rowActions={rowActions}
          onRowClick={handleRowClick}
          border
        />
      </div>
      <Modal
        open={modalOpen}
        onHide={() => setModalOpen(false)}
        title={atomicCategory?._id ? 'Edit Category' : 'Add Category'}
      >
        <ModalContent>
          <h2 className={classes.modalHeader}>{(atomicCategory?._id && 'Edit Category') || 'Add Company'}</h2>
          <div className={classes.modalContent}>
            <TextInput
              value={atomicCategory?.atomicCategory}
              onChange={(value) => setAtomicCategory({ ...atomicCategory, atomicCategory: value })}
              labelText="Category Name"
              required
              type="text"
            />
            <div>
              <label className={classes.label} htmlFor="description">
                Description
              </label>
              <SimpleTextArea
                id="description"
                value={atomicCategory?.description}
                onChange={(value) => setAtomicCategory({ ...atomicCategory, description: value })}
                required
              />
            </div>
          </div>
        </ModalContent>
        <ModalFooter>
          <Button type="button" onClick={() => setModalOpen(false)} link>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={createLoading || updateLoading}
            primary
            loading={createLoading || updateLoading}
            loadingText="Saving..."
            onClick={handleSave}
          >
            Save
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default CategoryManager;
